import { useCallback, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  fetchAutocompletePredictions,
  fetchPlaceDetails,
  AutocompletePrediction,
  PlaceResult,
} from 'app/integrations/google-places';
import { ErrorMessage, getErrorMessage } from 'utils/axios';
import { notification } from 'antd';
import { NotificationProps } from 'utils/types';

interface UsePlacesAutocompleteConfig {
  debounce?: number;
  options?: {
    componentRestrictions?: { country: string };
  };
}

interface UsePlacesAutocompleteReturn {
  placePredictions: AutocompletePrediction[];
  getPlacePredictions: (request: { input: string; types?: string[] }) => void;
  isPlacePredictionsLoading: boolean;
  getPlaceDetails: (
    request: { placeId: string; fields?: string[] },
    callback: (result: PlaceResult | null, status: string) => void,
  ) => void;
  isPlaceDetailsLoading: boolean;
}

/**
 * Custom hook that uses Places API (New) REST service for autocomplete
 * This replaces the deprecated AutocompleteService with the new Autocomplete API
 *
 * Uses React Query for efficient data fetching and caching
 *
 * Reference: https://developers.google.com/maps/documentation/places/web-service/place-autocomplete
 */
export function usePlacesAutocomplete(config: UsePlacesAutocompleteConfig = {}): UsePlacesAutocompleteReturn {
  const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  // Use React Query mutation for autocomplete predictions
  const autocompleteMutation = useMutation({
    mutationFn: async (params: { input: string; types?: string[] }) => {
      return fetchAutocompletePredictions(params.input, {
        apiKey,
        countryRestriction: config.options?.componentRestrictions?.country,
        types: params.types,
      });
    },
    onSuccess: (data) => {
      setPredictions(data);
    },
    onError: (error: ErrorMessage) => {
      notification.error({
        message: getErrorMessage(error),
        ...NotificationProps,
      });
      setPredictions([]);
    },
  });

  // Use React Query mutation for place details
  const placeDetailsMutation = useMutation({
    mutationFn: async (params: { placeId: string; fields?: string[] }) => {
      return fetchPlaceDetails(params, apiKey);
    },
  });

  const getPlacePredictions = useCallback(
    (request: { input: string; types?: string[] }) => {
      // Clear any pending debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Return empty results for empty input
      if (!request.input || request.input.trim().length === 0) {
        setPredictions([]);
        return;
      }

      if (!apiKey) {
        setPredictions([]);
        return;
      }

      const executeSearch = () => {
        autocompleteMutation.mutate(request);
      };

      // Apply debounce if configured
      if (config.debounce && config.debounce > 0) {
        debounceTimerRef.current = setTimeout(executeSearch, config.debounce);
      } else {
        executeSearch();
      }
    },
    [apiKey, config.debounce, autocompleteMutation],
  );

  /**
   * Fetches place details by place ID
   * Maintains callback pattern for backward compatibility with existing components
   *
   * @param request - Place ID and optional fields to fetch
   * @param callback - Callback function with result and status
   */
  const getPlaceDetails = useCallback(
    (
      request: { placeId: string; fields?: string[] },
      callback: (result: PlaceResult | null, status: string) => void,
    ) => {
      if (!apiKey) {
        callback(null, 'ERROR');
        return;
      }

      placeDetailsMutation.mutate(request, {
        onSuccess: (data) => {
          callback(data, 'OK');
        },
        onError: () => {
          callback(null, 'ERROR');
        },
      });
    },
    [apiKey, placeDetailsMutation],
  );

  return {
    placePredictions: predictions,
    getPlacePredictions,
    isPlacePredictionsLoading: autocompleteMutation.isPending,
    getPlaceDetails,
    isPlaceDetailsLoading: placeDetailsMutation.isPending,
  };
}
